import { useEffect, useState, Fragment } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, fmtUSD, formatApiError } from "../lib/api";
import { Package, ShoppingBag, Users, FileText, TrendingUp, Tag, Plus, Trash2, ChevronDown, ChevronRight, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
    const { user, checking } = useAuth();
    if (checking) return <div className="pt-52 text-center text-secondary">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    return (
        <div data-testid="admin-page" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="mb-10">
                    <div className="data-label text-brand mb-3">CONTROL CENTER</div>
                    <h1 className="font-serif text-5xl">Aminoplex Admin</h1>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <aside className="lg:col-span-3">
                        <AdminNav />
                    </aside>
                    <main className="lg:col-span-9">
                        <Routes>
                            <Route index element={<AnalyticsPanel />} />
                            <Route path="orders" element={<OrdersPanel />} />
                            <Route path="products" element={<ProductsPanel />} />
                            <Route path="coas" element={<CoasPanel />} />
                            <Route path="customers" element={<CustomersPanel />} />
                            <Route path="subscribers" element={<SubscribersPanel />} />
                            <Route path="coupons" element={<CouponsPanel />} />
                        </Routes>
                    </main>
                </div>
            </div>
        </div>
    );
}

function AdminNav() {
    const loc = useLocation();
    const items = [
        { to: "/admin", label: "Analytics", icon: TrendingUp },
        { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
        { to: "/admin/products", label: "Products", icon: Package },
        { to: "/admin/coas", label: "COAs", icon: FileText },
        { to: "/admin/customers", label: "Customers", icon: Users },
        { to: "/admin/subscribers", label: "Subscribers", icon: CreditCard },
        { to: "/admin/coupons", label: "Coupons", icon: Tag },
    ];
    return (
        <nav className="space-y-1 lg:sticky lg:top-32">
            {items.map((it) => {
                const Icon = it.icon;
                const active = loc.pathname === it.to;
                return (
                    <Link key={it.to} to={it.to} data-testid={`admin-nav-${it.label.toLowerCase()}`} className={`flex items-center gap-3 px-4 py-3 border ${active ? "border-[var(--brand)] text-brand bg-[var(--brand-soft)]" : "border-transparent text-secondary hover:text-primary"}`}>
                        <Icon size={14} />
                        <span className="font-mono text-xs uppercase tracking-wider">{it.label}</span>
                    </Link>
                );
            })}
        </nav>
    );
}

function AnalyticsPanel() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="text-secondary">Loading…</div>;
    return (
        <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Revenue" value={fmtUSD(data.revenue)} brand />
                <Stat label="Orders" value={data.total_orders} />
                <Stat label="Customers" value={data.customers} />
                <Stat label="Active Products" value={data.active_products} />
                <Stat label="Pending Payment" value={data.pending_payment} />
                <Stat label="Paid" value={data.paid} />
                <Stat label="Shipped" value={data.shipped} />
                <Stat label="Low Stock" value={data.low_stock.length} />
            </div>

            {data.low_stock.length > 0 && (
                <div className="card p-6">
                    <h3 className="font-serif text-2xl mb-4">Low Stock</h3>
                    <div className="space-y-2 font-mono text-sm">
                        {data.low_stock.map((p) => (
                            <div key={p.id} className="flex justify-between py-2 border-b border-[var(--border)] last:border-0">
                                <span>{p.name}</span>
                                <span className="text-[var(--error)]">{p.stock} left</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="card p-6">
                <h3 className="font-serif text-2xl mb-4">Recent Orders</h3>
                <div className="space-y-2">
                    {data.recent_orders.map((o) => (
                        <div key={o.id} className="flex justify-between py-2 border-b border-[var(--border)] last:border-0 font-mono text-xs">
                            <span className="text-brand">{o.order_number}</span>
                            <span className="text-secondary">{o.status}</span>
                            <span>{fmtUSD(o.total)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, brand }) {
    return (
        <div className="card p-5">
            <div className="data-label">{label}</div>
            <div className={`font-serif text-3xl mt-2 ${brand ? "text-brand" : ""}`}>{value}</div>
        </div>
    );
}

function OrdersPanel() {
    const [orders, setOrders] = useState([]);
    const [expanded, setExpanded] = useState(null);
    const [detail, setDetail] = useState({});
    const [trackingDraft, setTrackingDraft] = useState("");
    const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
    useEffect(() => { load(); }, []);

    const fetchDetail = async (id) => {
        if (detail[id]) return;
        try {
            const { data } = await api.get(`/admin/orders/${id}`);
            setDetail((prev) => ({ ...prev, [id]: data }));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const toggle = async (id) => {
        if (expanded === id) { setExpanded(null); return; }
        setExpanded(id);
        setTrackingDraft("");
        await fetchDetail(id);
    };
    const update = async (id, status, tracking = "") => {
        try {
            await api.put(`/admin/orders/${id}`, { status, tracking });
            toast.success(status === "shipped"
                ? "Order marked shipped — shipping email sent"
                : "Order updated");
            setDetail((prev) => { const n = { ...prev }; delete n[id]; return n; });
            if (expanded === id) await fetchDetail(id);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div>
            <h2 className="font-serif text-3xl mb-6">All Orders</h2>
            <div className="card overflow-x-auto">
                <table className="w-full font-mono text-xs">
                    <thead className="border-b border-[var(--border)]">
                        <tr>{["", "Order", "Customer", "Items", "Total", "Method", "Status", "Update"].map((h) => (
                            <th key={h} className="text-left px-4 py-3 data-label">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {orders.map((o) => (
                            <Fragment key={o.id}>
                                <tr data-testid={`admin-order-row-${o.order_number}`} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-surface)] cursor-pointer" onClick={() => toggle(o.id)}>
                                    <td className="px-2 py-3 text-secondary">{expanded === o.id ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</td>
                                    <td className="px-4 py-3 text-brand">{o.order_number}</td>
                                    <td className="px-4 py-3 text-secondary">{o.shipping?.name || o.email}</td>
                                    <td className="px-4 py-3 text-secondary">{o.items?.length || 0}</td>
                                    <td className="px-4 py-3 text-primary">{fmtUSD(o.total)}</td>
                                    <td className="px-4 py-3 text-secondary">{o.payment_method?.toUpperCase()}</td>
                                    <td className="px-4 py-3">
                                        <span className="pill pill-gold">{o.status?.toUpperCase()}</span>
                                    </td>
                                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                        <select onChange={(e) => update(o.id, e.target.value)} className="input py-1 text-[0.65rem]" defaultValue="">
                                            <option value="" disabled>Change</option>
                                            <option value="pending_payment">Pending Payment</option>
                                            <option value="paid">Paid</option>
                                            <option value="shipped">Shipped</option>
                                            <option value="cancelled">Cancelled</option>
                                            <option value="refunded">Refunded</option>
                                        </select>
                                    </td>
                                </tr>
                                {expanded === o.id && (
                                    <tr data-testid={`admin-order-detail-${o.order_number}`} className="border-b border-[var(--border)] bg-[var(--bg-surface)]">
                                        <td colSpan={8} className="px-6 py-5">
                                            <OrderDetailBody d={detail[o.id]} trackingDraft={trackingDraft} setTrackingDraft={setTrackingDraft} onShip={(t) => update(o.id, "shipped", t)} />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function OrderDetailBody({ d, trackingDraft, setTrackingDraft, onShip }) {
    if (!d) return <div className="text-secondary text-xs font-mono">Loading order…</div>;
    const s = d.shipping || {};
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs font-mono">
            <div>
                <div className="data-label text-brand mb-2">CUSTOMER</div>
                <div className="text-primary">{d.customer?.name || s.name || "—"}</div>
                <div className="text-secondary">{d.customer?.email || d.email}</div>
                {d.customer?.membership_tier && (
                    <div className="mt-2 pill pill-gold inline-block">{d.customer.membership_tier} · {d.customer.membership_billing}</div>
                )}
                {s.phone && <div className="text-secondary mt-2">📞 {s.phone}</div>}
            </div>
            <div>
                <div className="data-label text-brand mb-2">SHIP TO</div>
                <div className="text-primary leading-relaxed">
                    {s.name}<br/>
                    {s.address}{s.address2 ? <><br/>{s.address2}</> : null}<br/>
                    {s.city}, {s.state} {s.zip}<br/>
                    {s.country}
                </div>
            </div>
            <div>
                <div className="data-label text-brand mb-2">PAYMENT</div>
                <div className="text-primary">{d.payment_method?.toUpperCase()}</div>
                {d.shipping_fee != null && (
                    <div className="text-secondary mt-1">Shipping: {fmtUSD(d.shipping_fee)}</div>
                )}
                {d.coupon?.code && <div className="text-secondary mt-1">Coupon: {d.coupon.code}</div>}
                <div className="text-brand font-serif text-xl mt-2">{fmtUSD(d.total)}</div>
            </div>
            <div className="md:col-span-3">
                <div className="data-label text-brand mb-2">ITEMS</div>
                <ul className="space-y-1">
                    {(d.items || []).map((it, i) => (
                        <li key={i} className="flex justify-between border-b border-[var(--border)] py-1">
                            <span className="text-primary">{it.name} × {it.quantity}</span>
                            <span className="text-secondary">{fmtUSD(it.line_total ?? (it.unit_price || 0) * it.quantity)}</span>
                        </li>
                    ))}
                </ul>
            </div>
            {d.notes && (
                <div className="md:col-span-3">
                    <div className="data-label text-brand mb-2">CUSTOMER NOTES</div>
                    <div className="text-primary">{d.notes}</div>
                </div>
            )}
            <div className="md:col-span-3 pt-2 border-t border-[var(--border)]">
                <div className="data-label text-brand mb-2">MARK SHIPPED + SEND TRACKING</div>
                <div className="flex gap-2 items-center">
                    <input
                        data-testid="admin-tracking-input"
                        value={trackingDraft}
                        onChange={(e) => setTrackingDraft(e.target.value)}
                        placeholder={d.tracking || "USPS / UPS / FedEx tracking number"}
                        className="input flex-1"
                    />
                    <button
                        data-testid="admin-ship-btn"
                        onClick={() => onShip(trackingDraft || d.tracking || "")}
                        disabled={!trackingDraft && !d.tracking}
                        className="btn btn-gold"
                    >
                        Mark Shipped & Email
                    </button>
                </div>
                {d.tracking && <div className="text-secondary mt-2">Current tracking: <span className="text-primary">{d.tracking}</span></div>}
            </div>
        </div>
    );
}

function SubscribersPanel() {
    const [subs, setSubs] = useState([]);
    useEffect(() => {
        api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, []);
    return (
        <div>
            <h2 className="font-serif text-3xl mb-6">Membership Subscribers</h2>
            {subs.length === 0 ? (
                <div className="card p-6 text-secondary text-sm">No active membership subscribers yet.</div>
            ) : (
                <div className="card overflow-x-auto">
                    <table className="w-full font-mono text-xs">
                        <thead className="border-b border-[var(--border)]">
                            <tr>{["Name", "Email", "Tier", "Billing", "Status", "Started"].map((h) => (
                                <th key={h} className="text-left px-4 py-3 data-label">{h}</th>
                            ))}</tr>
                        </thead>
                        <tbody>
                            {subs.map((s) => (
                                <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="border-b border-[var(--border)] last:border-0">
                                    <td className="px-4 py-3 text-primary">{s.name || "—"}</td>
                                    <td className="px-4 py-3 text-secondary">{s.email}</td>
                                    <td className="px-4 py-3"><span className="pill pill-gold">{(s.tier || "").toUpperCase()}</span></td>
                                    <td className="px-4 py-3 text-secondary">{s.billing || "—"}</td>
                                    <td className="px-4 py-3 text-secondary">{(s.status || "active").toUpperCase()}</td>
                                    <td className="px-4 py-3 text-secondary">{s.started_at ? new Date(s.started_at).toLocaleDateString() : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ProductsPanel() {
    const [products, setProducts] = useState([]);
    const load = () => api.get("/products").then((r) => setProducts(r.data));
    useEffect(() => { load(); }, []);
    const [editing, setEditing] = useState(null);

    const remove = async (id) => {
        if (!window.confirm("Deactivate this product?")) return;
        await api.delete(`/admin/products/${id}`);
        toast.success("Deactivated");
        load();
    };

    const updatePrice = async (p, raw) => {
        const price = parseFloat(raw);
        if (Number.isNaN(price) || price < 0) { toast.error("Invalid price"); return; }
        if (price === p.price) return;
        try {
            // Send full payload — backend PUT replaces. Pull everything we have on hand.
            const { data: fresh } = await api.get(`/products/${p.slug}`);
            await api.put(`/admin/products/${p.id}`, { ...fresh, price });
            toast.success(`${p.name}: ${fmtUSD(price)}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const updateStock = async (p, raw) => {
        const stock = parseInt(raw);
        if (Number.isNaN(stock) || stock < 0) { toast.error("Invalid stock"); return; }
        if (stock === p.stock) return;
        try {
            const { data: fresh } = await api.get(`/products/${p.slug}`);
            await api.put(`/admin/products/${p.id}`, { ...fresh, stock });
            toast.success(`${p.name}: ${stock} in stock`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-3xl">Products</h2>
                <button onClick={() => setEditing({ new: true, name: "", slug: "", category: "", price: 0, stock: 0, strength: "", vial_size: "", image: "", is_active: true, is_featured: false })} className="btn btn-gold">
                    <Plus size={14} /> Add Product
                </button>
            </div>
            <p className="data-label mb-4 text-[0.6rem] text-muted">Tip: click directly on a price or stock value to edit it inline. Hit Tab or click away to save.</p>

            {editing && <ProductEditor product={editing} onClose={() => { setEditing(null); load(); }} />}

            <div className="card overflow-x-auto">
                <table className="w-full font-mono text-xs">
                    <thead className="border-b border-[var(--border)]">
                        <tr>{["Product", "Slug", "Cat", "Price", "Stock", "Actions"].map((h) => (
                            <th key={h} className="text-left px-4 py-3 data-label">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {products.map((p) => (
                            <tr key={p.id} className="border-b border-[var(--border)] last:border-0">
                                <td className="px-4 py-3 text-primary">{p.name}<div className="text-muted">{p.strength}</div></td>
                                <td className="px-4 py-3 text-secondary">{p.slug}</td>
                                <td className="px-4 py-3 text-secondary">{p.category}</td>
                                <td className="px-4 py-3">
                                    <InlineNumber
                                        testid={`price-${p.slug}`}
                                        value={p.price}
                                        prefix="$"
                                        onSave={(v) => updatePrice(p, v)}
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <InlineNumber
                                        testid={`stock-${p.slug}`}
                                        value={p.stock}
                                        onSave={(v) => updateStock(p, v)}
                                    />
                                </td>
                                <td className="px-4 py-3 flex gap-2">
                                    <button onClick={() => setEditing(p)} className="btn btn-ghost">Edit</button>
                                    <button onClick={() => remove(p.id)} className="btn btn-ghost"><Trash2 size={12} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function InlineNumber({ value, onSave, prefix = "", testid }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(value));
    useEffect(() => { setDraft(String(value)); }, [value]);
    const commit = () => {
        setEditing(false);
        if (String(value) !== draft) onSave(draft);
    };
    if (editing) {
        return (
            <input
                data-testid={`edit-${testid}`}
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") commit();
                    if (e.key === "Escape") { setDraft(String(value)); setEditing(false); }
                }}
                className="input py-1 px-2 text-xs w-20"
            />
        );
    }
    return (
        <button
            data-testid={`inline-${testid}`}
            onClick={() => setEditing(true)}
            className="text-brand hover:bg-[var(--bg-surface)] px-2 py-1 -mx-2 -my-1 border border-transparent hover:border-[var(--brand)] transition-colors cursor-text text-left"
            title="Click to edit"
        >
            {prefix}{value}
        </button>
    );
}

function ProductEditor({ product, onClose }) {
    const [p, setP] = useState({ ...product });
    const set = (k, v) => setP({ ...p, [k]: v });
    const save = async () => {
        try {
            const payload = {
                name: p.name, slug: p.slug, category: p.category,
                short_description: p.short_description || "", description: p.description || "",
                price: parseFloat(p.price) || 0, stock: parseInt(p.stock) || 0,
                strength: p.strength || "", vial_size: p.vial_size || "",
                image: p.image || "", molecular_render: p.molecular_render || p.image || "",
                aliases: p.aliases || [], storage: p.storage || "", reconstitution: p.reconstitution || "",
                research_info: p.research_info || "", research_studies: p.research_studies || [],
                specifications: p.specifications || {}, batch_number: p.batch_number || "",
                purity: p.purity || "≥99%", is_featured: !!p.is_featured, is_active: p.is_active !== false,
            };
            if (p.new) await api.post("/admin/products", payload);
            else await api.put(`/admin/products/${p.id}`, payload);
            toast.success("Saved");
            onClose();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div className="card p-6 mb-6">
            <h3 className="font-serif text-2xl mb-4">{p.new ? "Add Product" : "Edit Product"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Inp label="Name" v={p.name} on={(v) => set("name", v)} />
                <Inp label="Slug" v={p.slug} on={(v) => set("slug", v)} />
                <Inp label="Category" v={p.category} on={(v) => set("category", v)} />
                <Inp label="Image URL" v={p.image} on={(v) => set("image", v)} />
                <Inp label="Price" v={p.price} on={(v) => set("price", v)} />
                <Inp label="Stock" v={p.stock} on={(v) => set("stock", v)} />
                <Inp label="Strength" v={p.strength} on={(v) => set("strength", v)} />
                <Inp label="Vial Size" v={p.vial_size} on={(v) => set("vial_size", v)} />
                <Inp label="Batch Number" v={p.batch_number} on={(v) => set("batch_number", v)} />
                <Inp label="Purity" v={p.purity} on={(v) => set("purity", v)} />
                <div className="md:col-span-2">
                    <label className="label">Short Description</label>
                    <textarea value={p.short_description || ""} onChange={(e) => set("short_description", e.target.value)} className="input" rows={2} />
                </div>
                <div className="md:col-span-2">
                    <label className="label">Long Description</label>
                    <textarea value={p.description || ""} onChange={(e) => set("description", e.target.value)} className="input" rows={3} />
                </div>
                <label className="flex items-center gap-2 data-label cursor-pointer">
                    <input type="checkbox" checked={!!p.is_featured} onChange={(e) => set("is_featured", e.target.checked)} /> Featured
                </label>
                <label className="flex items-center gap-2 data-label cursor-pointer">
                    <input type="checkbox" checked={p.is_active !== false} onChange={(e) => set("is_active", e.target.checked)} /> Active
                </label>
            </div>
            <div className="flex gap-3 mt-5">
                <button onClick={save} className="btn btn-gold">Save</button>
                <button onClick={onClose} className="btn">Cancel</button>
            </div>
        </div>
    );
}

function Inp({ label, v, on }) {
    return (
        <div>
            <label className="label">{label}</label>
            <input value={v ?? ""} onChange={(e) => on(e.target.value)} className="input" />
        </div>
    );
}

function CoasPanel() {
    const [coas, setCoas] = useState([]);
    const [form, setForm] = useState({ product_slug: "", product_name: "", batch_number: "", test_date: "", purity: "", test_method: "HPLC + Mass Spectrometry", lab_name: "Janoshik Analytical (Independent)", pdf_url: "", notes: "" });
    const load = () => api.get("/coas").then((r) => setCoas(r.data));
    useEffect(() => { load(); }, []);
    const add = async () => {
        try {
            await api.post("/admin/coas", form);
            toast.success("COA added");
            setForm({ ...form, batch_number: "", purity: "", pdf_url: "", notes: "" });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (id) => {
        await api.delete(`/admin/coas/${id}`);
        load();
    };
    return (
        <div>
            <h2 className="font-serif text-3xl mb-6">COA Manager</h2>
            <div className="card p-6 mb-6">
                <div className="data-label text-brand mb-4">UPLOAD COA</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Inp label="Product Slug" v={form.product_slug} on={(v) => setForm({ ...form, product_slug: v })} />
                    <Inp label="Product Name" v={form.product_name} on={(v) => setForm({ ...form, product_name: v })} />
                    <Inp label="Batch Number" v={form.batch_number} on={(v) => setForm({ ...form, batch_number: v })} />
                    <Inp label="Test Date (YYYY-MM-DD)" v={form.test_date} on={(v) => setForm({ ...form, test_date: v })} />
                    <Inp label="Purity (%)" v={form.purity} on={(v) => setForm({ ...form, purity: v })} />
                    <Inp label="Lab Name" v={form.lab_name} on={(v) => setForm({ ...form, lab_name: v })} />
                    <Inp label="PDF URL" v={form.pdf_url} on={(v) => setForm({ ...form, pdf_url: v })} />
                    <Inp label="Notes" v={form.notes} on={(v) => setForm({ ...form, notes: v })} />
                </div>
                <button onClick={add} className="btn btn-gold mt-4"><Plus size={14} /> Add COA</button>
            </div>

            <div className="card overflow-x-auto">
                <table className="w-full font-mono text-xs">
                    <thead className="border-b border-[var(--border)]">
                        <tr>{["Product", "Batch", "Purity", "Date", "Lab", "Actions"].map((h) => (
                            <th key={h} className="text-left px-4 py-3 data-label">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {coas.map((c) => (
                            <tr key={c.id} className="border-b border-[var(--border)] last:border-0">
                                <td className="px-4 py-3">{c.product_name}</td>
                                <td className="px-4 py-3 text-brand">{c.batch_number}</td>
                                <td className="px-4 py-3 text-brand">{c.purity}</td>
                                <td className="px-4 py-3 text-secondary">{c.test_date}</td>
                                <td className="px-4 py-3 text-secondary">{c.lab_name}</td>
                                <td className="px-4 py-3"><button onClick={() => remove(c.id)} className="btn btn-ghost"><Trash2 size={12} /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CustomersPanel() {
    const [users, setUsers] = useState([]);
    useEffect(() => { api.get("/admin/customers").then((r) => setUsers(r.data)); }, []);
    return (
        <div>
            <h2 className="font-serif text-3xl mb-6">Customers</h2>
            <p className="data-label text-muted mb-4">Researcher qualification data captured at registration — for Stripe / underwriting review.</p>
            <div className="card overflow-x-auto" data-testid="admin-customers-table">
                <table className="w-full font-mono text-xs">
                    <thead className="border-b border-[var(--border)]">
                        <tr>{["Email", "Name", "Organization", "Category", "Field of Research", "Age 21+", "RUO", "Terms", "Joined"].map((h) => (
                            <th key={h} className="text-left px-4 py-3 data-label whitespace-nowrap">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-b border-[var(--border)] last:border-0 align-top">
                                <td className="px-4 py-3 text-brand whitespace-nowrap">{u.email}</td>
                                <td className="px-4 py-3">{u.name}</td>
                                <td className="px-4 py-3 text-secondary">{u.organization || <span className="text-muted">—</span>}</td>
                                <td className="px-4 py-3 text-secondary">{u.buyer_category || <span className="text-muted">—</span>}</td>
                                <td className="px-4 py-3 text-secondary">{u.research_field || <span className="text-muted">—</span>}</td>
                                <td className={`px-4 py-3 ${u.age_21_plus ? "text-brand" : "text-muted"}`}>{u.age_21_plus ? "✓" : "—"}</td>
                                <td className={`px-4 py-3 ${u.ruo_attestation ? "text-brand" : "text-muted"}`}>{u.ruo_attestation ? "✓" : "—"}</td>
                                <td className={`px-4 py-3 ${u.terms_accepted ? "text-brand" : "text-muted"}`}>{u.terms_accepted ? "✓" : "—"}</td>
                                <td className="px-4 py-3 text-secondary whitespace-nowrap">{u.created_at?.slice(0, 10)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CouponsPanel() {
    const [coupons, setCoupons] = useState([]);
    const empty = {
        code: "", discount_type: "percent", percent_off: 10, amount_off: 0,
        description: "", is_active: true, one_time_per_user: false,
        min_subtotal: 0, max_uses: 0, expires_at: "",
    };
    const [form, setForm] = useState(empty);
    const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
    useEffect(() => { load(); }, []);
    const add = async () => {
        try {
            const payload = { ...form, code: form.code.trim().toUpperCase() };
            if (!payload.code) { toast.error("Code is required"); return; }
            // Convert empty expires_at → null so backend doesn't try to parse it
            if (!payload.expires_at) payload.expires_at = null;
            await api.post("/admin/coupons", payload);
            toast.success(`Coupon ${payload.code} created`);
            setForm(empty);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this coupon?")) return;
        await api.delete(`/admin/coupons/${id}`); load();
    };
    const discountLabel = (c) => c.discount_type === "fixed"
        ? `$${(c.amount_off || 0).toFixed(2)} off`
        : `${c.percent_off || 0}% off`;
    return (
        <div>
            <h2 className="font-serif text-3xl mb-6">Coupons</h2>
            <div className="card p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Inp label="Code" v={form.code} on={(v) => setForm({ ...form, code: v.toUpperCase() })} />
                <div>
                    <label className="label">Type</label>
                    <select
                        value={form.discount_type}
                        onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                        className="input"
                    >
                        <option value="percent">Percent off</option>
                        <option value="fixed">Fixed $ off</option>
                    </select>
                </div>
                {form.discount_type === "percent" ? (
                    <Inp label="% Off" v={form.percent_off} on={(v) => setForm({ ...form, percent_off: parseFloat(v) || 0 })} />
                ) : (
                    <Inp label="$ Off" v={form.amount_off} on={(v) => setForm({ ...form, amount_off: parseFloat(v) || 0 })} />
                )}
                <Inp label="Min Subtotal ($)" v={form.min_subtotal} on={(v) => setForm({ ...form, min_subtotal: parseFloat(v) || 0 })} />
                <Inp label="Max Uses (0 = unlimited)" v={form.max_uses} on={(v) => setForm({ ...form, max_uses: parseInt(v) || 0 })} />
                <div>
                    <label className="label">Expires (optional)</label>
                    <input
                        type="date"
                        value={form.expires_at ? form.expires_at.slice(0, 10) : ""}
                        onChange={(e) => setForm({ ...form, expires_at: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
                        className="input"
                    />
                </div>
                <div className="md:col-span-2"><Inp label="Description (shown at checkout)" v={form.description} on={(v) => setForm({ ...form, description: v })} /></div>
                <label className="flex items-center gap-2 text-xs text-secondary md:col-span-2">
                    <input type="checkbox" checked={form.one_time_per_user} onChange={(e) => setForm({ ...form, one_time_per_user: e.target.checked })} />
                    One-time use per customer (requires sign-in)
                </label>
                <button data-testid="create-coupon-btn" onClick={add} className="btn btn-gold md:col-span-2"><Plus size={14} /> Create Coupon</button>
            </div>
            <div className="card overflow-x-auto">
                <table className="w-full font-mono text-xs">
                    <thead className="border-b border-[var(--border)]">
                        <tr>{["Code", "Discount", "Min $", "Uses", "Expires", "Active", ""].map((h) => (
                            <th key={h} className="text-left px-4 py-3 data-label">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-[var(--border)]">
                            <td className="px-4 py-3 text-brand">WELCOME10</td>
                            <td className="px-4 py-3">10%</td>
                            <td className="px-4 py-3 text-secondary">—</td>
                            <td className="px-4 py-3 text-secondary">1/customer</td>
                            <td className="px-4 py-3 text-muted">Never</td>
                            <td className="px-4 py-3 text-brand">Built-in</td>
                            <td className="px-4 py-3 text-muted">—</td>
                        </tr>
                        {coupons.map((c) => (
                            <tr key={c.id} data-testid={`coupon-row-${c.code}`} className="border-b border-[var(--border)] last:border-0">
                                <td className="px-4 py-3 text-brand">{c.code}</td>
                                <td className="px-4 py-3">{discountLabel(c)}</td>
                                <td className="px-4 py-3 text-secondary">{c.min_subtotal ? `$${c.min_subtotal}` : "—"}</td>
                                <td className="px-4 py-3 text-secondary">{c.max_uses ? `${c.usage_count || 0}/${c.max_uses}` : (c.usage_count || 0)}</td>
                                <td className="px-4 py-3 text-secondary">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                                <td className="px-4 py-3">{c.is_active ? <span className="text-brand">Yes</span> : <span className="text-muted">No</span>}</td>
                                <td className="px-4 py-3"><button onClick={() => remove(c.id)} className="btn btn-ghost"><Trash2 size={12} /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
